package features.booking

/**
 * Tipo de día en el que opera un punto de subida / parada.
 *
 * El catálogo guardaba un solo horario por punto, así que para tener corridas
 * distintas entre semana y en sábado había que duplicar el punto con otro nombre
 * ("Plaza Zielo Zibata" vs 'Plaza Zielo Zibata"'). Con este campo el mismo lugar
 * puede existir dos veces pero queda etiquetado, y al programar solo se ofrecen
 * los que aplican al día del viaje.
 *
 * Los registros viejos no tienen el campo: se leen como TODOS para no romperlos.
 */
object TipoDia {
    const val TODOS = "todos"
    const val SEMANA = "semana"   // lunes a viernes
    const val SABADO = "sabado"   // sábados (y domingo, si algún día operan)

    val OPCIONES = listOf(TODOS, SEMANA, SABADO)

    fun normalizar(valor: String): String = when (valor.trim().lowercase()) {
        SEMANA, "entre semana", "lv", "l-v" -> SEMANA
        SABADO, "sábado", "sabados", "sábados", "finde", "fin de semana", "fines de semana" -> SABADO
        else -> TODOS
    }

    fun etiqueta(valor: String): String = when (normalizar(valor)) {
        SEMANA -> "Entre semana"
        SABADO -> "Fines de semana"
        else -> "Todos los días"
    }

    fun etiquetaCorta(valor: String): String = when (normalizar(valor)) {
        SEMANA -> "L-V"
        SABADO -> "Sáb"
        else -> "Todos"
    }
}

private val NOMBRES_DIA = listOf("Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado")
private val NOMBRES_DIA_CORTO = listOf("dom", "lun", "mar", "mié", "jue", "vie", "sáb")
private val NOMBRES_MES_CORTO = listOf("ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic")

/**
 * Día de la semana de una fecha "yyyy-MM-dd": 0 = domingo … 6 = sábado. -1 si no parsea.
 * Algoritmo de Sakamoto — puro Kotlin para que funcione igual en Android, iOS y Web
 * sin depender de la librería de fechas de cada plataforma.
 */
fun diaSemanaDe(fecha: String): Int {
    val partes = fecha.trim().split("-")
    if (partes.size != 3) return -1
    val anio = partes[0].toIntOrNull() ?: return -1
    val mes = partes[1].toIntOrNull() ?: return -1
    val dia = partes[2].toIntOrNull() ?: return -1
    if (mes !in 1..12 || dia !in 1..31) return -1
    val t = intArrayOf(0, 3, 2, 5, 0, 3, 5, 1, 4, 6, 2, 4)
    val y = if (mes < 3) anio - 1 else anio
    return (y + y / 4 - y / 100 + y / 400 + t[mes - 1] + dia) % 7
}

fun esSabado(fecha: String): Boolean = diaSemanaDe(fecha) == 6

fun esEntreSemana(fecha: String): Boolean = diaSemanaDe(fecha) in 1..5

/** Tipo de día al que pertenece una fecha concreta (sábado y domingo cuentan como fin de semana). */
fun tipoDiaDeFecha(fecha: String): String {
    val d = diaSemanaDe(fecha)
    return if (d == 6 || d == 0) TipoDia.SABADO else TipoDia.SEMANA
}

/** ¿Este punto (con su etiqueta de días) aplica para un viaje de esa fecha? */
fun puntoAplicaEnFecha(diasDelPunto: String, fecha: String): Boolean {
    val tipo = TipoDia.normalizar(diasDelPunto)
    return tipo == TipoDia.TODOS || tipo == tipoDiaDeFecha(fecha)
}

/** ¿Este punto aplica para un tipo de día objetivo? TODOS aplica siempre. */
fun puntoAplicaEnTipo(diasDelPunto: String, tipoObjetivo: String): Boolean {
    val tipo = TipoDia.normalizar(diasDelPunto)
    return tipo == TipoDia.TODOS || tipo == TipoDia.normalizar(tipoObjetivo)
}

/**
 * Primer sábado a partir de [desde]. Con [incluirMismoDia] = false nunca devuelve
 * el mismo día: si hoy es sábado, salta al de la semana siguiente.
 */
fun proximoSabado(desde: String, incluirMismoDia: Boolean = true): String {
    val d = diaSemanaDe(desde)
    if (d < 0) return desde
    var faltan = (6 - d + 7) % 7
    if (faltan == 0 && !incluirMismoDia) faltan = 7
    return org.example.abigail.calcularFechaFutura(desde, faltan)
}

/** Primer día entre semana (lunes a viernes) a partir de [desde]. */
fun proximoDiaEntreSemana(desde: String): String {
    var fecha = desde
    var intentos = 0
    while (!esEntreSemana(fecha) && intentos < 7) {
        fecha = org.example.abigail.calcularFechaFutura(fecha, 1)
        intentos++
    }
    return fecha
}

/** Genera [cantidad] sábados consecutivos empezando en el primer sábado desde [desde]. */
fun sabadosDesde(desde: String, cantidad: Int): List<String> {
    if (cantidad <= 0) return emptyList()
    val primero = proximoSabado(desde)
    return (0 until cantidad).map { org.example.abigail.calcularFechaFutura(primero, it * 7) }
}

/** Genera los días lunes-viernes de [semanas] semanas, empezando en el primer hábil desde [desde]. */
fun diasEntreSemanaDesde(desde: String, semanas: Int): List<String> {
    if (semanas <= 0) return emptyList()
    val resultado = mutableListOf<String>()
    var fecha = proximoDiaEntreSemana(desde)
    var restantes = semanas * 5
    var guardia = 0
    while (restantes > 0 && guardia < semanas * 7 + 14) {
        if (esEntreSemana(fecha)) {
            resultado.add(fecha)
            restantes--
        }
        fecha = org.example.abigail.calcularFechaFutura(fecha, 1)
        guardia++
    }
    return resultado
}

fun nombreDiaDe(fecha: String): String {
    val d = diaSemanaDe(fecha)
    return if (d in 0..6) NOMBRES_DIA[d] else ""
}

/** "sáb 20 sep" — para listar las fechas que se van a publicar sin ocupar medio renglón. */
fun fechaCorta(fecha: String): String {
    val partes = fecha.trim().split("-")
    if (partes.size != 3) return fecha
    val mes = partes[1].toIntOrNull() ?: return fecha
    val dia = partes[2].toIntOrNull() ?: return fecha
    val d = diaSemanaDe(fecha)
    val nombreDia = if (d in 0..6) NOMBRES_DIA_CORTO[d] else ""
    val nombreMes = if (mes in 1..12) NOMBRES_MES_CORTO[mes - 1] else ""
    return "$nombreDia $dia $nombreMes".trim()
}
